import { useEffect, useRef, useState } from "react";
import { App } from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import CopyIcon from "@/assets/copy.svg?react";
import { getAppSecret, resetAppSecret } from "@/api/apps";
import { IconBtn } from "@/components/IconBtn";
import { brandModalConfirmProps } from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { credentialErrorText } from "./apiError";
import { MASK, type ResetHours } from "./constants";
import { CredentialField } from "./CredentialField";
import { ResetSecretModal } from "./ResetSecretModal";

const HIDE_AFTER_MS = 30_000;

export function AppSecretField() {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const app = useCurrentApp();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetHours, setResetHours] = useState<ResetHours>(1);
  const [secret, setSecret] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hintOpen = resetOpen ? false : undefined;
  const appId = app.app_id || app.id;

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setRevealed(false), HIDE_AFTER_MS);
  };

  const loadSecret = async () => {
    if (secret) return secret;
    setBusy(true);
    try {
      const data = await getAppSecret(appId);
      setSecret(data.app_secret);
      return data.app_secret;
    } catch (err) {
      message.error(credentialErrorText(t, err));
      return "";
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const value = await loadSecret();
    if (!value) return;
    await navigator.clipboard?.writeText(value);
    message.success(t("credentials.copiedSecret"));
  };

  const confirmReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      const data = await resetAppSecret(appId);
      setSecret(data.app_secret);
      setRevealed(true);
      scheduleHide();
      setResetOpen(false);
      message.success(t("credentials.resetDone"));
    } catch (err) {
      message.error(credentialErrorText(t, err));
    } finally {
      setResetting(false);
    }
  };

  const reveal = () => {
    modal.confirm({
      title: t("credentials.appSecret"),
      content: t("credentials.revealConfirm"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      ...brandModalConfirmProps(),
      onOk: async () => {
        const value = await loadSecret();
        if (!value) return;
        setRevealed(true);
        scheduleHide();
      },
    });
  };

  return (
    <>
      <CredentialField
        label={t("credentials.appSecret")}
        hint={t("credentials.appSecretHint")}
      >
        <span className="font-mono text-sm tracking-wide text-primary">
          {revealed && secret ? secret : MASK}
        </span>
        <IconBtn
          label={t("common.copy")}
          hintOpen={hintOpen}
          onClick={() => {
            if (!busy) void copy();
          }}
        >
          <CopyIcon />
        </IconBtn>
        <IconBtn
          label={revealed ? t("common.hide") : t("common.show")}
          hintOpen={hintOpen}
          onClick={() => {
            if (busy) return;
            if (revealed) {
              setRevealed(false);
              return;
            }
            reveal();
          }}
        >
          {revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </IconBtn>
        <IconBtn
          label={t("credentials.resetNow")}
          hintOpen={hintOpen}
          onClick={() => {
            if (!busy && !resetting) setResetOpen(true);
          }}
        >
          <SyncOutlined />
        </IconBtn>
      </CredentialField>

      <ResetSecretModal
        open={resetOpen}
        hours={resetHours}
        confirming={resetting}
        onHoursChange={setResetHours}
        onCancel={() => {
          if (!resetting) setResetOpen(false);
        }}
        onConfirm={() => {
          void confirmReset();
        }}
      />
    </>
  );
}
